import React from "react";
import { useQuery } from "react-query";
import axios from "axios";

export interface IChatMessage {
  id: number;
  sender: "user" | "ai";
  text: string | React.ReactNode;
  createdAt: string;
  articleLink?: string;
  fromPrompt?: boolean;
  wasHelpful?: boolean;
}

export interface IChatStore {
  resetHistory: () => void;
  appendToHistory: (message: IChatMessage) => void;
  setChatId: (chatId: number) => void;
}

// Utility function for loading chat history
export const useChatHistory = (
  queryKeyPrefix: string,
  chatType: string,
  deviceId: string,
  store: IChatStore,
  OpeningMessageComponent: React.FC,
  api: ReturnType<typeof axios.create>
) => {
  return useQuery(
    [`${queryKeyPrefix}-chat-history`, deviceId],
    async () => {
      const response = await api.get<{
        chatId: number;
        messages: Array<{
          id: number;
          sender: "user" | "ai";
          text: string;
          createdAt: string;
          fromPrompt: boolean;
          articleSuggestions?: Array<{ title: string; url: string }>;
          wasHelpful?: boolean;
        }>;
      }>(`/v1/ai-chats/messages/by-deviceId/${deviceId}?type=${chatType}`);
      return response.data;
    },
    {
      onSuccess: (data) => {
        // Always reset history first
        store.resetHistory();

        // Always add the welcome message first
        const welcomeMessage: IChatMessage = {
          id: 0, // Use lowest ID to ensure it appears first
          sender: "ai",
          text: <OpeningMessageComponent />,
          createdAt: new Date(0).toISOString(), // Use earliest date
        };
        store.appendToHistory(welcomeMessage);

        // Then, if history was loaded, add those messages
        if (data.chatId && data.messages && data.messages.length > 0) {
          // Set the chat ID
          store.setChatId(data.chatId);

          // Transform messages from API format to frontend format
          const messages: IChatMessage[] = data.messages.map((msg) => ({
            id: msg.id,
            sender: msg.sender,
            text: msg.text,
            createdAt: msg.createdAt,
            articleLink: msg.articleSuggestions ? JSON.stringify(msg.articleSuggestions) : undefined,
            fromPrompt: msg.fromPrompt,
            wasHelpful: msg.wasHelpful,
          }));

          // Add the loaded chat messages after the welcome message
          messages.forEach((message) => {
            store.appendToHistory(message);
          });
        }
      },
      onError: (error) => {
        console.error(`Failed to load chat history for device ${deviceId}, type ${chatType}:`, error);
        // In case of error, reset and add the welcome message
        store.resetHistory();
        const welcomeMessage: IChatMessage = {
          id: Date.now(),
          sender: "ai",
          text: <OpeningMessageComponent />,
          createdAt: new Date().toISOString(),
        };
        store.appendToHistory(welcomeMessage);
      },
      staleTime: Infinity, // Only fetch once per session
      cacheTime: Infinity,
      retry: false, // Don't retry on failure
    }
  );
};
